import {useCallback, useEffect, useState} from 'react'
import {UseTencentCaptchaResult, TencentCaptchaResponse} from '_types'
import {useGlobalConfigState} from '_context'

/**
 * @name useTencentCaptcha
 *
 * @hook
 *
 * @description
 * Downloads the Tencent recaptcha script by catching url and appId by custom preferences
 * and expose methods and vars to handle the Tencent captcha
 *
 * @returns {UseTencentCaptchaResult} Rendered locale modal.
 */
const useTencentCaptcha = (): UseTencentCaptchaResult => {
    const [isTencentReady, setIsTencentReady] = useState(false)
    const [isTencentEnabled, setIsTencentEnabled] = useState<boolean | undefined>(undefined)
    const [tencentCaptchaResult, setTencentCaptchaResult] = useState<TencentCaptchaResponse | null>(null)
    const [tencentRetCode, setTencentRetCode] = useState<number | null>(null)
    const [tencentError, setTencentError] = useState<Error | null>(null)

    const {appPreferences} = useGlobalConfigState()
    const {TencentCaptchaScriptUrl, TencentCaptchaAppID} = appPreferences
    const appId = TencentCaptchaAppID
    const scriptUrl = TencentCaptchaScriptUrl

    useEffect(() => {
        if (!appId) {
            setIsTencentEnabled(false)
        } else if (appId && appId.length > 0 && scriptUrl) {
            setIsTencentEnabled(true)
            const existingScript = document.getElementById('tencent-captcha')

            if (!existingScript) {
                const script = document.createElement('script')
                script.src = scriptUrl
                script.id = 'tencent-captcha'
                script.async = true

                script.onload = () => {
                    setIsTencentReady(true)
                }

                script.onerror = () => {
                    const err = new Error('Errore nel caricamento di TencentCaptcha.js')
                    console.error(err.message)
                    setTencentError(err)
                }

                document.body.appendChild(script)
            } else {
                setIsTencentReady(true)
            }
        }
    }, [appId, scriptUrl])

    const triggerTencentCaptcha = useCallback(() => {
        setTencentError(null)
        setTencentCaptchaResult(null)
        setTencentRetCode(null)
        if (window.TencentCaptcha && appId) {
            const captcha = new window.TencentCaptcha(appId, (res: TencentCaptchaResponse) => {
                setTencentRetCode(res.ret)
                if (res.ret === 0) {
                    setTencentCaptchaResult(res)
                } else {
                    console.error('Captcha Failed:', res)
                }
            })

            captcha.show()
        } else {
            const err = new Error('TencentCaptcha not Available in window')
            console.error(err.message)
            setTencentError(err)
        }
    }, [appId])

    const resetTencentCaptcha = () => {
        setTencentCaptchaResult(null)
        setTencentRetCode(null)
        setTencentError(null)
    }

    return {
        isTencentReady,
        isTencentEnabled,
        tencentCaptchaResult,
        tencentRetCode,
        tencentError,
        triggerTencentCaptcha,
        resetTencentCaptcha,
    }
}

export {useTencentCaptcha}
